import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, ArrowLeft, Rocket, Target, Wallet, Users, Image as ImageIcon, Check, Sparkles } from "lucide-react";

const STEPS = [
  { icon: Target, label: "Goal" },
  { icon: Wallet, label: "Budget" },
  { icon: Users, label: "Audience" },
  { icon: ImageIcon, label: "Creative" },
  { icon: Check, label: "Review" },
];

const GENDERS = [["all", "Everyone"], ["male", "Men"], ["female", "Women"]];

export default function CreateAdWizard({ accountId, onClose, onCreated }) {
  const [meta, setMeta] = useState({ objectives: [], ctas: [], special_ad_categories: [] });
  const [pages, setPages] = useState([]);
  const [step, setStep] = useState(0);
  const [advanced, setAdvanced] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [form, setForm] = useState({
    campaign_name: "",
    objective: "OUTCOME_TRAFFIC",
    special_ad_category: "NONE",
    budget_type: "daily",
    budget_amount: 20,
    start_time: "",
    end_time: "",
    countries: "US",
    age_min: 18,
    age_max: 65,
    genders: "all",
    page_id: "",
    message: "",
    headline: "",
    description: "",
    image_url: "",
    link: "",
    call_to_action: "LEARN_MORE",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const [m, p] = await Promise.all([
          axios.get(`${API}/meta/objectives`),
          axios.get(`${API}/meta/ad-accounts/${accountId}/pages`),
        ]);
        setMeta(m.data);
        setPages(p.data.pages || []);
        if ((p.data.pages || []).length) set("page_id", p.data.pages[0].id);
      } catch (e) { /* handled on publish */ }
    })();
  }, [accountId]);

  const loadPreview = async () => {
    if (!form.page_id) return toast.error("Select a Facebook Page first");
    setPreviewing(true);
    setPreviewHtml("");
    try {
      const res = await axios.post(`${API}/meta/ad-accounts/${accountId}/preview`, {
        page_id: form.page_id, message: form.message, headline: form.headline || null,
        description: form.description || null, image_url: form.image_url || null,
        link: form.link || "https://facebook.com", call_to_action: form.call_to_action,
      });
      setPreviewHtml(res.data.html || "");
      if (!res.data.html) toast.error("Preview came back empty");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate preview");
    } finally { setPreviewing(false); }
  };

  const publish = async () => {
    if (!form.campaign_name) return toast.error("Give your campaign a name");
    if (!form.page_id) return toast.error("Select a Facebook Page for your ad");
    if (!form.message || !form.link) return toast.error("Add your ad text and a destination URL");
    if (form.budget_type === "lifetime" && !form.end_time) return toast.error("Lifetime budgets need an end date");
    setPublishing(true);
    try {
      const payload = {
        campaign_name: form.campaign_name,
        objective: form.objective,
        special_ad_category: form.special_ad_category,
        budget_type: form.budget_type,
        budget_amount: Number(form.budget_amount),
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        targeting: {
          countries: form.countries.split(",").map((c) => c.trim().toUpperCase()).filter(Boolean),
          age_min: Number(form.age_min),
          age_max: Number(form.age_max),
          genders: form.genders,
        },
        creative: {
          page_id: form.page_id,
          message: form.message,
          headline: form.headline || null,
          description: form.description || null,
          image_url: form.image_url || null,
          link: form.link,
          call_to_action: form.call_to_action,
        },
      };
      const res = await axios.post(`${API}/meta/ad-accounts/${accountId}/create-ad`, payload);
      toast.success("Ad created (paused) — review and activate when ready!");
      onCreated && onCreated(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create ad");
    } finally {
      setPublishing(false);
    }
  };

  const Field = ({ label, help, children }) => (
    <div className="form-group" style={{ margin: "0 0 16px" }}>
      <label className="form-label">{label}</label>
      {children}
      {help && <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>{help}</div>}
    </div>
  );

  const GoalStep = (
    <div>
      <Field label="Campaign name"><input className="form-input" data-testid="wiz-name" value={form.campaign_name} onChange={(e) => set("campaign_name", e.target.value)} placeholder="e.g. June — Book a Demo" /></Field>
      <label className="form-label">What's your goal?</label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: "12px", margin: "8px 0 16px" }}>
        {meta.objectives.map((o) => (
          <button key={o.value} data-testid={`wiz-goal-${o.value}`} onClick={() => set("objective", o.value)}
            style={{ textAlign: "left", padding: "14px", borderRadius: "12px", cursor: "pointer",
              border: form.objective === o.value ? "2px solid #7c3aed" : "1px solid #e2e8f0",
              background: form.objective === o.value ? "#f5f3ff" : "#fff" }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>{o.label}</div>
            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>{o.help}</div>
          </button>
        ))}
      </div>
      <Field label="Special Ad Category" help="Required by Meta for credit, employment, housing, financial or political ads.">
        <select className="form-select" data-testid="wiz-category" value={form.special_ad_category} onChange={(e) => set("special_ad_category", e.target.value)}>
          {meta.special_ad_categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </Field>
    </div>
  );

  const BudgetStep = (
    <div>
      <Field label="Budget type">
        <div style={{ display: "flex", gap: "8px" }}>
          {[["daily", "Daily budget"], ["lifetime", "Lifetime budget"]].map(([v, l]) => (
            <button key={v} data-testid={`wiz-budget-${v}`} onClick={() => set("budget_type", v)}
              style={{ padding: "8px 16px", borderRadius: "9px", fontWeight: 600, cursor: "pointer",
                border: form.budget_type === v ? "1px solid #7c3aed" : "1px solid #e2e8f0",
                background: form.budget_type === v ? "#f5f3ff" : "#fff", color: form.budget_type === v ? "#6d28d9" : "#64748b" }}>{l}</button>
          ))}
        </div>
      </Field>
      <Field label={`Amount (USD) — ${form.budget_type}`} help="Minimum $1.00. You won't be charged until you activate the ad.">
        <input className="form-input" type="number" min="1" step="1" data-testid="wiz-amount" value={form.budget_amount} onChange={(e) => set("budget_amount", e.target.value)} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <Field label="Start (optional)"><input className="form-input" type="datetime-local" data-testid="wiz-start" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} /></Field>
        <Field label={form.budget_type === "lifetime" ? "End (required)" : "End (optional)"}><input className="form-input" type="datetime-local" data-testid="wiz-end" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} /></Field>
      </div>
    </div>
  );

  const AudienceStep = (
    <div>
      <Field label="Countries" help="Comma-separated ISO codes, e.g. US, CA, GB.">
        <input className="form-input" data-testid="wiz-countries" value={form.countries} onChange={(e) => set("countries", e.target.value)} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <Field label="Min age"><input className="form-input" type="number" min="13" max="65" data-testid="wiz-agemin" value={form.age_min} onChange={(e) => set("age_min", e.target.value)} /></Field>
        <Field label="Max age"><input className="form-input" type="number" min="13" max="65" data-testid="wiz-agemax" value={form.age_max} onChange={(e) => set("age_max", e.target.value)} /></Field>
      </div>
      <Field label="Gender">
        <div style={{ display: "flex", gap: "8px" }}>
          {GENDERS.map(([v, l]) => (
            <button key={v} data-testid={`wiz-gender-${v}`} onClick={() => set("genders", v)}
              style={{ padding: "8px 16px", borderRadius: "9px", fontWeight: 600, cursor: "pointer",
                border: form.genders === v ? "1px solid #7c3aed" : "1px solid #e2e8f0",
                background: form.genders === v ? "#f5f3ff" : "#fff", color: form.genders === v ? "#6d28d9" : "#64748b" }}>{l}</button>
          ))}
        </div>
      </Field>
      <div style={{ background: "#eff6ff", borderLeft: "3px solid #2563eb", borderRadius: "6px", padding: "10px 12px", fontSize: "13px", color: "#1e40af" }}>
        <Sparkles size={13} style={{ verticalAlign: "-2px", marginRight: "6px" }} />
        Placements are set to <strong>Advantage+ (automatic)</strong> so Meta shows your ad where it performs best across Facebook &amp; Instagram.
      </div>
    </div>
  );

  const CreativeStep = (
    <div>
      <Field label="Facebook Page" help={pages.length ? "Your ad runs from this Page." : "No Pages found — make sure you granted Page access when connecting Meta."}>
        <select className="form-select" data-testid="wiz-page" value={form.page_id} onChange={(e) => set("page_id", e.target.value)}>
          <option value="">Select a Page…</option>
          {pages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <Field label="Primary text" help="The main message people see above your image."><textarea className="form-input" rows={3} data-testid="wiz-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Stop losing leads to slow follow-up…" /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <Field label="Headline"><input className="form-input" data-testid="wiz-headline" value={form.headline} onChange={(e) => set("headline", e.target.value)} placeholder="Book a free demo" /></Field>
        <Field label="Description"><input className="form-input" data-testid="wiz-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="See Revo in action" /></Field>
      </div>
      <Field label="Image URL" help="Public direct link to a JPG/PNG (recommended 1080×1080 or 1200×628)."><input className="form-input" data-testid="wiz-image" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…/ad.jpg" /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "16px" }}>
        <Field label="Destination URL"><input className="form-input" data-testid="wiz-link" value={form.link} onChange={(e) => set("link", e.target.value)} placeholder="https://revodigitaltx.com/demo" /></Field>
        <Field label="Button">
          <select className="form-select" data-testid="wiz-cta" value={form.call_to_action} onChange={(e) => set("call_to_action", e.target.value)}>
            {meta.ctas.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
      </div>
    </div>
  );

  const ReviewStep = (
    <div data-testid="wiz-review">
      {[
        ["Campaign", form.campaign_name],
        ["Goal", meta.objectives.find((o) => o.value === form.objective)?.label],
        ["Budget", `$${form.budget_amount} ${form.budget_type}`],
        ["Audience", `${form.countries} · ${form.age_min}-${form.age_max} · ${GENDERS.find((g) => g[0] === form.genders)?.[1]}`],
        ["Page", pages.find((p) => p.id === form.page_id)?.name || "—"],
        ["Destination", form.link],
        ["Button", form.call_to_action.replace(/_/g, " ")],
      ].map(([k, v]) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
          <span style={{ color: "#64748b", fontSize: "13px" }}>{k}</span>
          <span style={{ fontWeight: 600, color: "#0f172a", fontSize: "13px", maxWidth: "60%", textAlign: "right", wordBreak: "break-word" }}>{v || "—"}</span>
        </div>
      ))}
      <div style={{ background: "#f0fdf4", borderLeft: "3px solid #16a34a", borderRadius: "6px", padding: "10px 12px", fontSize: "13px", color: "#166534", marginTop: "14px" }}>
        Everything is created <strong>paused</strong>. Nothing spends until you activate it from the campaigns list.
      </div>
      <div style={{ marginTop: "16px" }}>
        <Button variant="outline" size="sm" data-testid="wiz-preview-btn" onClick={loadPreview} disabled={previewing}>
          {previewing ? "Loading preview…" : "👁 Preview ad on Facebook/Instagram"}
        </Button>
        {previewHtml && (
          <div data-testid="wiz-preview" style={{ marginTop: "12px", border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", display: "flex", justifyContent: "center", background: "#f8fafc" }}
            dangerouslySetInnerHTML={{ __html: previewHtml }} />
        )}
      </div>
    </div>
  );

  const stepContent = [GoalStep, BudgetStep, AudienceStep, CreativeStep, ReviewStep];

  return (
    <div data-testid="create-ad-wizard" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: "18px", width: "100%", maxWidth: "700px", boxShadow: "0 24px 70px rgba(0,0,0,0.25)" }}>
        {/* Header */}
        <div style={{ padding: "22px 26px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ display: "inline-flex", padding: "7px", borderRadius: "10px", background: "linear-gradient(135deg,#1877F2,#C13584)", color: "#fff" }}><Rocket size={18} /></span>
            <h2 style={{ fontSize: "18px", fontWeight: 800 }}>Create Ad</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <label style={{ fontSize: "12px", color: "#64748b", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
              <input type="checkbox" data-testid="wiz-advanced-toggle" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} /> Show all fields
            </label>
            <X size={22} style={{ cursor: "pointer", color: "#64748b" }} onClick={onClose} data-testid="wiz-close" />
          </div>
        </div>

        {/* Stepper */}
        {!advanced && (
          <div style={{ display: "flex", gap: "6px", padding: "16px 26px 0" }}>
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ height: "4px", borderRadius: "4px", background: i <= step ? "linear-gradient(90deg,#7c3aed,#4f46e5)" : "#e2e8f0", marginBottom: "8px" }} />
                  <div style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "11px", fontWeight: 600, color: i === step ? "#6d28d9" : "#94a3b8" }}>
                    <Icon size={13} /> {s.label}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Body */}
        <div style={{ padding: "22px 26px", maxHeight: "56vh", overflowY: "auto" }}>
          {advanced ? (
            <>{GoalStep}{BudgetStep}{AudienceStep}{CreativeStep}</>
          ) : stepContent[step]}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 26px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between" }}>
          <Button variant="outline" onClick={() => (step === 0 ? onClose() : setStep(step - 1))} data-testid="wiz-back" style={{ visibility: advanced ? "hidden" : "visible" }}>
            {step === 0 ? "Cancel" : <><ArrowLeft size={15} style={{ marginRight: "6px" }} /> Back</>}
          </Button>
          {advanced || step === STEPS.length - 1 ? (
            <Button onClick={publish} disabled={publishing} data-testid="wiz-publish" style={{ background: "linear-gradient(135deg,#7c3aed,#4f46e5)" }}>
              <Rocket size={15} style={{ marginRight: "6px" }} /> {publishing ? "Creating…" : "Create (paused)"}
            </Button>
          ) : (
            <Button onClick={() => setStep(step + 1)} data-testid="wiz-next" style={{ background: "linear-gradient(135deg,#7c3aed,#4f46e5)" }}>
              Next <ArrowRight size={15} style={{ marginLeft: "6px" }} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
